<template>
  <div class="p-4 max-w-6xl mx-auto space-y-6">
    <div class="bg-white border border-slate-200 rounded-xl shadow-sm p-4 space-y-4">
      <div class="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h2 class="text-2xl font-bold text-slate-900">🗂️ Horarios de Esteticistas</h2>
          <p class="text-sm text-slate-500">
            Filtra por esteticista, aplica plantillas y visualiza la agenda en tiempo real.
          </p>
        </div>
        <div class="flex items-center gap-2">
          <button
            class="px-4 py-2 text-sm font-semibold rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            :disabled="!selectedEnfermeroId && !mostrarTodos"
            @click="fetchDisponibilidad"
          >
            Refrescar calendario
          </button>
          <button
            class="px-4 py-2 text-sm font-semibold rounded-lg"
            :class="mostrarTodos ? 'bg-slate-900 text-white' : 'bg-emerald-600 text-white hover:bg-emerald-700'"
            @click="verTodos"
          >
            {{ mostrarTodos ? "Modo global activo" : "Ver todos" }}
          </button>
        </div>
      </div>

      <div
        v-if="estado.mensaje"
        class="rounded-lg px-4 py-2 text-sm font-semibold flex items-center gap-2"
        :class="estado.tipo === 'success'
          ? 'bg-emerald-50 text-emerald-700'
          : estado.tipo === 'error'
            ? 'bg-red-50 text-red-600'
            : 'bg-slate-50 text-slate-600'"
      >
        <span>{{ estado.mensaje }}</span>
        <button class="ml-auto text-xs hover:underline" @click="estado.mensaje = ''">
          Cerrar
        </button>
      </div>

      <div class="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div class="relative w-full lg:max-w-sm">
          <input
            v-model="busqueda"
            type="text"
            class="w-full border border-slate-300 rounded-lg py-2 pl-10 pr-10 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400"
            placeholder="Buscar esteticista..."
          />
          <svg
            class="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M21 21l-4.35-4.35M11 19a8 8 0 100-16 8 8 0 000 16z"
            />
          </svg>
          <button
            v-if="busqueda"
            class="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
            @click="busqueda = ''"
          >
            ✕
          </button>
        </div>
        <div class="flex flex-wrap gap-2">
          <button
            v-for="enf in enfermerosFiltrados"
            :key="enf.id"
            class="px-3 py-1 rounded-full border text-sm font-medium transition"
            :class="
              selectedEnfermeroId === enf.id
                ? 'border-emerald-500 bg-emerald-50 text-emerald-700'
                : 'border-slate-200 bg-slate-50 text-slate-600 hover:bg-slate-100'
            "
            @click="setActiveEnfermero(enf.id)"
          >
            <span class="inline-flex items-center gap-2">
              <span
                class="w-2 h-2 rounded-full"
                :style="{ backgroundColor: enf.color }"
              ></span>
              {{ enf.nombreCorto }}
            </span>
          </button>
          <p v-if="!enfermerosFiltrados.length" class="text-xs text-slate-400">
            No hay resultados para “{{ busqueda }}”.
          </p>
        </div>
      </div>
    </div>

    <div class="bg-white rounded-xl border border-slate-200 shadow-sm p-4 flex flex-wrap gap-3">
      <button
        class="px-4 py-2 rounded-lg text-sm font-semibold text-white bg-slate-900 hover:bg-slate-800 disabled:opacity-40"
        :disabled="purificando || (!mostrarTodos && !selectedEnfermeroId)"
        @click="purificarHorarios"
      >
        {{ purificando ? "Purificando..." : "Purificar horarios" }}
      </button>
      <button
        class="px-4 py-2 rounded-lg text-sm font-semibold border border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-40"
        :disabled="verificandoResumen || (!mostrarTodos && !selectedEnfermeroId)"
        @click="cargarResumen"
      >
        {{ verificandoResumen ? "Auditando..." : "Auditar disponibilidad" }}
      </button>
      <div class="flex items-center gap-2 text-xs font-semibold text-slate-500">
        <span class="uppercase tracking-wide">
          {{ mostrarTodos ? "Modo global" : selectedEnfermero ? "Gestionando" : "Sin selección" }}
        </span>
        <span
          class="px-2 py-1 rounded-full"
          :class="mostrarTodos ? 'bg-slate-900 text-white' : selectedEnfermero ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-400'"
        >
          {{ mostrarTodos ? "Global" : selectedEnfermero?.nombreCorto || "Pendiente" }}
        </span>
      </div>
      <div class="flex items-center gap-2 ml-auto text-xs text-slate-500" v-if="resumenPlantilla">
        <span>Bloques futuros: <strong>{{ resumenPlantilla.bloquesFuturos }}</strong></span>
        <span>Próxima: <strong>{{ formatearResumenFecha(resumenPlantilla.primeraDisponibilidad) }}</strong></span>
      </div>
    </div>

    <div
      v-if="selectedEnfermeroId"
      class="border border-slate-200 rounded-xl bg-white shadow-sm"
    >
      <div class="flex items-center justify-between p-4 border-b border-slate-100">
        <div>
          <p class="text-xs uppercase font-semibold text-slate-400">Plantilla semanal</p>
          <h3 class="text-lg font-semibold text-slate-900">
            {{ selectedEnfermero?.nombreCompleto || "Selecciona un esteticista" }}
          </h3>
          <p class="text-xs text-slate-500">
            Ajusta los días base y regenera futuras semanas sólo cuando lo necesites.
          </p>
        </div>
        <button
          class="px-3 py-1.5 rounded-lg text-sm font-semibold border border-slate-300 text-slate-700 hover:bg-slate-50"
          @click="mostrarConfigurador = !mostrarConfigurador"
        >
          {{ mostrarConfigurador ? "Ocultar plantilla" : "Editar plantilla" }}
        </button>
      </div>
      <div v-if="mostrarConfigurador" class="p-4">
        <p class="text-xs text-slate-500 mb-3" v-if="aplicandoPlantilla">
          Aplicando cambios...
        </p>
        <HorarioPlantilla
          :usuario-id="selectedEnfermeroId"
          @plantilla-guardada="aplicarPlantilla"
        />
      </div>
    </div>

    <div v-if="!selectedEnfermeroId && !mostrarTodos" class="text-center text-gray-500 py-16 border rounded bg-white">
      Selecciona un esteticista o activa el modo global para ver la disponibilidad.
    </div>

    <div v-else class="space-y-4">
      <div class="bg-white border border-slate-200 rounded-xl shadow-sm p-4">
        <p class="text-sm text-slate-500">
          Cada tarjeta muestra la agenda de un esteticista. Expande solo lo que necesites para evitar listas interminables.
        </p>
      </div>

      <div v-if="tarjetasEnfermeros.length" class="space-y-4">
        <section
          v-for="enf in tarjetasEnfermeros"
          :key="enf.id"
          class="border border-slate-200 rounded-xl bg-white shadow-sm"
        >
          <header class="flex flex-col gap-3 md:flex-row md:items-center md:justify-between p-4 border-b border-slate-100">
            <div>
              <p class="text-xs uppercase font-semibold text-slate-400">
                Esteticista
              </p>
              <h4 class="text-xl font-semibold text-slate-900 flex items-center gap-2">
                <span class="w-2.5 h-2.5 rounded-full" :style="{ backgroundColor: enf.color }"></span>
                {{ enf.nombreCompleto }}
              </h4>
              <p class="text-xs text-slate-500">
                {{ resumenRapido(enf.id).descripcion }}
              </p>
            </div>
            <div class="flex flex-wrap gap-2">
              <span class="px-3 py-1 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700">
                {{ resumenRapido(enf.id).bloques }} bloques
              </span>
              <span class="px-3 py-1 rounded-full text-xs font-semibold bg-slate-50 text-slate-600">
                Próxima: {{ resumenRapido(enf.id).proxima }}
              </span>
              <button
                class="px-3 py-1 rounded-lg text-sm font-semibold border border-slate-300 text-slate-700 hover:bg-slate-50"
                @click="togglePanel(enf.id)"
              >
                {{ panelAbierto === enf.id ? "Ocultar agenda" : "Ver agenda" }}
              </button>
              <button
                class="px-3 py-1 rounded-lg text-sm font-semibold border border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                @click="setActiveEnfermero(enf.id)"
              >
                Gestionar
              </button>
            </div>
          </header>

          <div v-show="panelAbierto === enf.id" class="p-4 space-y-4">
            <div v-if="obtenerDiasOrdenados(enf.id).length" class="space-y-4">
              <article
                v-for="fecha in obtenerDiasOrdenados(enf.id)"
                :key="fecha"
                class="border border-slate-100 rounded-lg p-3 space-y-3"
              >
                <div class="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                  <div>
                    <p class="text-xs uppercase font-semibold text-slate-400">
                      {{ formatearFechaHumana(fecha) }}
                    </p>
                    <p class="text-lg font-semibold text-slate-900">{{ fecha }}</p>
                  </div>
                  <div class="flex flex-wrap gap-2">
                    <button
                      class="px-3 py-1.5 rounded-lg text-sm font-semibold border border-amber-300 text-amber-700 hover:bg-amber-50 disabled:opacity-40"
                      :disabled="bloqueando"
                      @click="pausarFecha(fecha, enf.id)"
                    >
                      {{ bloqueando ? "Pausando..." : "Pausar día" }}
                    </button>
                    <button
                      class="px-3 py-1.5 rounded-lg text-sm font-semibold border border-emerald-300 text-emerald-700 hover:bg-emerald-50 disabled:opacity-40"
                      :disabled="habilitando"
                      @click="habilitarFecha(fecha, enf.id)"
                    >
                      {{ habilitando ? "Habilitando..." : "Habilitar día" }}
                    </button>
                  </div>
                </div>

                <div v-if="obtenerBloquesDia(enf.id, fecha).length" class="space-y-3">
                  <div
                    v-for="bloque in obtenerBloquesDia(enf.id, fecha)"
                    :key="bloque.id"
                    class="border border-slate-100 rounded-lg p-3 space-y-3"
                  >
                    <div class="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                      <div>
                        <p class="text-base font-semibold text-slate-900">
                          {{ formatearHoraBloque(bloque.start) }} – {{ formatearHoraBloque(bloque.end) }}
                        </p>
                        <p class="text-xs text-slate-500">
                          {{ etiquetaOrigen(bloque.origen) }}
                        </p>
                      </div>
                      <div class="flex flex-wrap gap-2">
                        <button
                          class="px-3 py-1.5 rounded-lg text-sm border border-slate-300 text-slate-700 hover:bg-slate-50"
                          @click="iniciarEdicion(bloque, enf.id)"
                        >
                          Editar
                        </button>
                        <button
                          class="px-3 py-1.5 rounded-lg text-sm border border-red-300 text-red-700 hover:bg-red-50"
                          @click="eliminarBloque(bloque)"
                        >
                          Eliminar
                        </button>
                      </div>
                    </div>

                    <div
                      v-if="edicionActiva === bloque.id"
                      class="grid gap-3 md:grid-cols-5"
                    >
                      <label class="flex flex-col gap-1 text-sm">
                        <span class="text-xs font-semibold text-slate-500">Desde</span>
                        <input
                          type="time"
                          v-model="formEdicion.desde"
                          class="border rounded-lg px-3 py-2 focus:ring-emerald-500 focus:border-emerald-500"
                        />
                      </label>
                      <label class="flex flex-col gap-1 text-sm">
                        <span class="text-xs font-semibold text-slate-500">Hasta</span>
                        <input
                          type="time"
                          v-model="formEdicion.hasta"
                          class="border rounded-lg px-3 py-2 focus:ring-emerald-500 focus:border-emerald-500"
                        />
                      </label>
                      <div class="flex items-end gap-2 md:col-span-3">
                        <button
                          class="flex-1 bg-emerald-600 text-white py-2 rounded-lg font-semibold hover:bg-emerald-700 disabled:opacity-40"
                          :disabled="guardandoBloque"
                          @click="guardarEdicion"
                        >
                          Guardar cambios
                        </button>
                        <button
                          class="flex-1 bg-slate-100 text-slate-700 py-2 rounded-lg font-semibold hover:bg-slate-200"
                          @click="cancelarEdicion"
                        >
                          Cancelar
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
                <p v-else class="text-sm text-slate-500">No hay bloques activos para esta fecha.</p>
              </article>
            </div>
            <p v-else class="text-sm text-slate-500">
              Este esteticista no tiene bloques en las semanas consultadas.
            </p>
          </div>
        </section>
      </div>

      <p v-else class="text-center text-slate-400 py-12">
        Aún no hay bloques generados en las semanas consultadas.
      </p>
    </div>

    <section class="bg-white border border-slate-200 rounded-xl shadow-sm p-4 space-y-4">
      <div class="flex flex-col gap-1">
        <h3 class="text-lg font-semibold text-slate-900">Vista timeline (solo lectura)</h3>
        <p class="text-sm text-slate-500">
          Revisa cómo se distribuyen los bloques de todos los esteticistas en la semana. Usa la rueda horizontal si necesitas ver todas las horas.
        </p>
      </div>
      <div v-if="timelineDias.length" class="space-y-6">
        <article
          v-for="dia in timelineDias"
          :key="dia.fecha"
          class="space-y-3"
        >
          <div class="flex items-center justify-between">
            <div>
              <p class="text-xs uppercase font-semibold text-slate-400">Fecha</p>
              <p class="text-base font-semibold text-slate-900">
                {{ formatearFechaHumana(dia.fecha) }} · {{ dia.fecha }}
              </p>
            </div>
            <span class="text-xs px-3 py-1 rounded-full bg-slate-100 text-slate-600 font-semibold">
              {{ dia.empleados.length }} esteticista(s)
            </span>
          </div>

          <div class="overflow-x-auto border border-slate-200 rounded-lg">
            <div class="min-w-[720px]">
              <div class="grid grid-cols-[180px_1fr] border-b border-slate-200 bg-slate-50">
                <div class="px-4 py-2 text-xs font-semibold uppercase text-slate-500">
                  Esteticista
                </div>
                <div class="flex">
                  <div
                    v-for="hora in horasTimeline"
                    :key="hora"
                    class="flex-1 text-center text-xs font-semibold text-slate-500 py-2 border-l border-slate-200"
                  >
                    {{ hora }}
                  </div>
                </div>
              </div>
              <div
                v-for="empleado in dia.empleados"
                :key="empleado.uid"
                class="grid grid-cols-[180px_1fr] border-b border-slate-100 last:border-b-0"
              >
                <div class="px-4 py-3 text-sm font-semibold text-slate-800">
                  {{ empleado.nombre }}
                </div>
                <div class="relative py-3 border-l border-slate-100">
                  <div class="timeline-row">
                    <div
                      v-for="bloque in empleado.bloques"
                      :key="bloque.id"
                      class="timeline-block"
                      :style="calcularBloqueStyle(bloque)"
                      :title="calcularBloqueLabel(bloque)"
                    >
                      <span>{{ calcularBloqueLabel(bloque) }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </article>
      </div>
      <p v-else class="text-sm text-slate-500">Carga una disponibilidad para visualizarla en el timeline.</p>
    </section>
  </div>
</template>

<script>
import { collection, getDocs } from "firebase/firestore";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { getFunctions, httpsCallable } from "firebase/functions";
import { db } from "@/firebase";
import { formatIsoPrTime, formatIsoPrDateKey, addDaysToDateKey } from "@/utils/prTime";
import HorarioPlantilla from "./HorarioPlantilla.vue";

const SEMANAS_GENERAR = 12;
const PALETA_COLORES = [
  "#10b981",
  "#3b82f6",
  "#f59e0b",
  "#ec4899",
  "#8b5cf6",
  "#0ea5e9",
  "#f97316",
];
const BLOQUES_ALERTA = 10;

export default {
  name: "AdminHorarios",
  components: { HorarioPlantilla },
  data() {
    return {
      enfermeros: [],
      selectedEnfermeroId: "",
      functionsInstance: null,
      eventos: [],
      disponibilidadPorEnfermero: {},
      busqueda: "",
      mostrarTodos: false,
      aplicandoPlantilla: false,
      estado: { mensaje: "", tipo: "info" },
      estadoTimeout: null,
      resumenPlantilla: null,
      bloquesAlerta: BLOQUES_ALERTA,
      verificandoResumen: false,
      diaParaBloquear: "",
      diaParaHabilitar: "",
      bloqueando: false,
      habilitando: false,
      purificando: false,
      authPromise: null,
      edicionActiva: null,
      mostrarConfigurador: false,
      guardandoBloque: false,
      panelAbierto: null,
      formEdicion: {
        desde: "",
        hasta: "",
        fecha: "",
        bloqueId: "",
        enfermeroId: "",
      },
      timelineConfig: { inicio: 7, fin: 21 },
    };
  },
  computed: {
    selectedEnfermero() {
      return this.enfermeros.find((e) => e.id === this.selectedEnfermeroId) || null;
    },
    enfermerosFiltrados() {
      if (!this.busqueda) return this.enfermeros;
      const termino = this.busqueda.toLowerCase();
      return this.enfermeros.filter((enf) =>
        enf.nombreCompleto.toLowerCase().includes(termino)
      );
    },
    tarjetasEnfermeros() {
      if (this.mostrarTodos) return this.enfermeros;
      return this.selectedEnfermero ? [this.selectedEnfermero] : [];
    },
    diasSeleccionados() {
      const uid = this.selectedEnfermeroId;
      if (!uid) return [];
      return this.obtenerDiasOrdenados(uid);
    },
    timelineDias() {
      const hoyKey = formatIsoPrDateKey(new Date());
      const finKey = hoyKey ? addDaysToDateKey(hoyKey, 6) : "";
      const mapa = {};
      (this.eventos || []).forEach((bloque) => {
        const fecha =
          bloque.fechaKey ||
          formatIsoPrDateKey(bloque.startIso || bloque.start || "") ||
          "";
        if (!fecha) return;
        if (hoyKey && finKey && (fecha < hoyKey || fecha > finKey)) return;
        if (!mapa[fecha]) mapa[fecha] = {};
        const uid = bloque.enfermeroId || "sin-uid";
        if (!mapa[fecha][uid]) mapa[fecha][uid] = [];
        mapa[fecha][uid].push(bloque);
      });
      return Object.entries(mapa)
        .sort(([a], [b]) => (a > b ? 1 : -1))
        .map(([fecha, porEmpleado]) => ({
          fecha,
          empleados: Object.entries(porEmpleado).map(([uid, bloques]) => ({
            uid,
            nombre: this.nombreEnfermero(uid),
            bloques: bloques
              .slice()
              .sort((a, b) => new Date(a.start || 0) - new Date(b.start || 0)),
          })),
        }));
    }
  },
  watch: {
    selectedEnfermeroId() {
      if (this.selectedEnfermeroId || this.mostrarTodos) {
        this.fetchDisponibilidad();
      } else {
        this.eventos = [];
        this.disponibilidadPorEnfermero = {};
      }
      this.mostrarConfigurador = false;
    },
    tarjetasEnfermeros: {
      handler(lista) {
        if (!lista.some((enf) => enf.id === this.panelAbierto)) {
          this.panelAbierto = null;
        }
      },
      immediate: true,
    },
  },
  methods: {
    getCallable(nombre) {
      if (!this.functionsInstance) {
        this.functionsInstance = getFunctions();
      }
      return httpsCallable(this.functionsInstance, nombre);
    },
    ensureAuth() {
      const auth = getAuth();
      if (auth.currentUser) {
        return Promise.resolve(auth.currentUser);
      }
      if (!this.authPromise) {
        this.authPromise = new Promise((resolve) => {
          const unsubscribe = onAuthStateChanged(auth, (user) => {
            unsubscribe();
            resolve(user);
          });
        });
      }
      return this.authPromise;
    },
    async invocarCallable(nombre, payload) {
      const usuario = await this.ensureAuth();
      if (!usuario) {
        throw new Error("Debes iniciar sesión para administrar horarios.");
      }
      const callable = this.getCallable(nombre);
      const respuesta = await callable(payload);
      return respuesta?.data;
    },
    mostrarEstado(mensaje, tipo = "info") {
      this.estado = { mensaje, tipo };
      clearTimeout(this.estadoTimeout);
      this.estadoTimeout = setTimeout(() => {
        this.estado.mensaje = "";
      }, 4000);
    },
    setActiveEnfermero(id) {
      if (this.selectedEnfermeroId === id) return;
      this.selectedEnfermeroId = id;
      this.mostrarTodos = false;
      this.fetchDisponibilidad();
    },
    verTodos() {
      this.mostrarTodos = true;
      this.selectedEnfermeroId = "";
      this.fetchDisponibilidad();
    },
    async cargarEnfermeros() {
      const snapshot = await getDocs(collection(db, "enfermeros"));
      this.enfermeros = snapshot.docs.map((docSnap, index) => {
        const data = docSnap.data() || {};
        const nombreCompleto = `${data.nombre || "Sin nombre"} ${data.apellido || ""}`.trim();
        const nombreCorto = (data.nombre || "Sin nombre").split(" ")[0];
        const color = PALETA_COLORES[index % PALETA_COLORES.length];
        return { id: docSnap.id, nombreCompleto, nombreCorto, color, ...data };
      });
      if (!this.selectedEnfermeroId && this.enfermeros.length) {
        this.selectedEnfermeroId = this.enfermeros[0].id;
      }
    },
    async fetchDisponibilidad() {
      const usuario = await this.ensureAuth();
      if (!usuario) return;
      const ids = this.mostrarTodos
        ? this.enfermeros.map((e) => e.id)
        : this.selectedEnfermeroId
        ? [this.selectedEnfermeroId]
        : [];
      if (!ids.length) return;
      try {
        const payload = await this.invocarCallable("listarDisponibilidadCallable", {
          uids: ids,
          soloFuturos: false,
          semanas: SEMANAS_GENERAR,
          origenesPermitidos: ["auto", "excepcion"],
        });
        const resultados = [];
        const porEnfermero = payload?.porEnfermero || {};
        Object.entries(porEnfermero).forEach(([uid, bloques = []]) => {
          bloques.forEach((data) => {
            const startIso = data.start;
            const endIso = data.end;
            const esClon = Boolean(data.esClon || data.autoGenerado);
            const color = this.colorPorEnfermero(data.enfermeroId || uid);
            resultados.push({
              id: data.id,
              ...data,
              startIso,
              endIso,
              start: startIso,
              end: endIso,
              title: data.title || "Disponible",
              esClon,
              color,
              textColor: "#ffffff"
            });
          });
        });
        this.eventos = resultados;
        this.actualizarDisponibilidadPorDia();
      } catch (error) {
        console.error("Error al cargar disponibilidad:", error);
        this.mostrarEstado("No se pudo cargar la disponibilidad seleccionada.", "error");
      } finally {
        if (!this.mostrarTodos && this.selectedEnfermeroId) {
          this.cargarResumen();
        } else {
          this.resumenPlantilla = null;
        }
      }
    },
    colorPorEnfermero(id) {
      const enf = this.enfermeros.find((e) => e.id === id);
      return enf?.color || "#3b82f6";
    },
    actualizarDisponibilidadPorDia() {
      const hoyKey = formatIsoPrDateKey(new Date());
      const finKey = hoyKey ? addDaysToDateKey(hoyKey, 6) : "";
      const mapa = {};
      (this.eventos || []).forEach((bloque) => {
        const uid = bloque.enfermeroId || "";
        if (!uid) return;
        if (!mapa[uid]) mapa[uid] = {};
        const fecha =
          bloque.fechaKey ||
          formatIsoPrDateKey(bloque.startIso || bloque.start || "") ||
          "";
        if (!fecha) return;
        if (hoyKey && finKey && (fecha < hoyKey || fecha > finKey)) return;
        if (!mapa[uid][fecha]) mapa[uid][fecha] = [];
        mapa[uid][fecha].push(bloque);
      });
      Object.keys(mapa).forEach((uid) => {
        Object.keys(mapa[uid]).forEach((fecha) => {
          mapa[uid][fecha].sort(
            (a, b) => new Date(a.start || 0).getTime() - new Date(b.start || 0).getTime()
          );
        });
      });
      this.disponibilidadPorEnfermero = mapa;
    },
    resumenRapido(uid) {
      const dias = this.disponibilidadPorEnfermero[uid] || {};
      const bloques = this.eventos.filter((b) => (b.enfermeroId || uid) === uid);
      const primera = bloques
        .slice()
        .sort((a, b) => new Date(a.start || 0) - new Date(b.start || 0))[0];
      const fechaKey = primera
        ? (primera.fechaKey || (primera.start || "").split("T")[0] || "")
        : "";
      const proxima = primera
        ? `${this.formatearFechaHumana(fechaKey)} · ${this.formatearHoraBloque(primera.start)}`
        : "N/A";
      return {
        bloques: bloques.length,
        proxima,
        descripcion: bloques.length
          ? `${Object.keys(dias).length} día(s) activos`
          : "Sin disponibilidad activa",
      };
    },
    togglePanel(id) {
      this.panelAbierto = this.panelAbierto === id ? null : id;
    },
    obtenerDiasOrdenados(uid) {
      const dias = this.disponibilidadPorEnfermero[uid] || {};
      return Object.keys(dias).sort();
    },
    obtenerBloquesDia(uid, fecha) {
      return (this.disponibilidadPorEnfermero[uid] || {})[fecha] || [];
    },
    async aplicarPlantilla() {
      if (!this.selectedEnfermeroId) return;
      this.aplicandoPlantilla = true;
      try {
        await this.invocarCallable("regenerarHorariosCallable", {
          uid: this.selectedEnfermeroId,
          semanas: SEMANAS_GENERAR,
        });
        await this.fetchDisponibilidad();
        this.mostrarEstado("Plantilla aplicada a las próximas semanas.", "success");
      } catch (error) {
        console.error("Error aplicando plantilla:", error);
        this.mostrarEstado("No se pudo aplicar la plantilla. Intenta nuevamente.", "error");
      } finally {
        this.aplicandoPlantilla = false;
      }
    },
    iniciarEdicion(bloque, enfermeroId = this.selectedEnfermeroId) {
      const fecha =
        bloque.fechaKey ||
        (bloque.startIso || bloque.start || "").split("T")[0] ||
        "";
      this.edicionActiva = bloque.id;
      this.formEdicion = {
        desde: formatIsoPrTime(bloque.start || bloque.startIso || "", {}),
        hasta: formatIsoPrTime(bloque.end || bloque.endIso || "", {}),
        fecha,
        bloqueId: bloque.id,
        enfermeroId: enfermeroId || bloque.enfermeroId || this.selectedEnfermeroId,
      };
    },
    cancelarEdicion() {
      this.edicionActiva = null;
      this.formEdicion = {
        desde: "",
        hasta: "",
        fecha: "",
        bloqueId: "",
        enfermeroId: "",
      };
    },
    async guardarEdicion() {
      if (!this.edicionActiva) return;
      const { desde, hasta, fecha, bloqueId, enfermeroId } = this.formEdicion;
      if (!desde || !hasta || !fecha) {
        this.mostrarEstado("Completa las horas antes de guardar.", "error");
        return;
      }
      if (desde >= hasta) {
        this.mostrarEstado("La hora final debe ser mayor a la inicial.", "error");
        return;
      }
      this.guardandoBloque = true;
      try {
        await this.invocarCallable("actualizarBloqueCallable", {
          uid: enfermeroId,
          disponibilidadId: bloqueId,
          start: `${fecha}T${desde}:00`,
          end: `${fecha}T${hasta}:00`,
        });
        this.mostrarEstado("Bloque actualizado.", "success");
        this.cancelarEdicion();
        await this.fetchDisponibilidad();
      } catch (error) {
        console.error("Error al actualizar bloque:", error);
        this.mostrarEstado("No se pudo actualizar el bloque.", "error");
      } finally {
        this.guardandoBloque = false;
      }
    },
    async eliminarBloque(bloque) {
      const uid = bloque.enfermeroId || this.selectedEnfermeroId;
      if (!uid) return;
      const confirmar = confirm("¿Eliminar este bloque disponible?");
      if (!confirmar) return;
      try {
        await this.invocarCallable("eliminarBloqueCallable", {
          uid,
          disponibilidadId: bloque.id,
        });
        this.mostrarEstado("Bloque eliminado correctamente.", "success");
        await this.fetchDisponibilidad();
      } catch (error) {
        console.error("Error al eliminar bloque:", error);
        this.mostrarEstado("No se pudo eliminar el bloque seleccionado.", "error");
      }
    },
    formatearFechaHumana(fecha) {
      if (!fecha) return "Sin fecha";
      const d = new Date(`${fecha}T12:00:00Z`);
      if (Number.isNaN(d.getTime())) return fecha;
      return d.toLocaleDateString("es-PR", {
        timeZone: "America/Puerto_Rico",
        weekday: "long",
        month: "short",
        day: "numeric",
      });
    },
    formatearHoraBloque(iso) {
      if (!iso) return "--";
      const hora = formatIsoPrTime(iso, { hour12: true });
      return hora || "--";
    },
    horasTimeline() {
      const horas = [];
      for (let h = this.timelineConfig.inicio; h <= this.timelineConfig.fin; h++) {
        const hora12 = ((h + 11) % 12) + 1;
        const suf = h >= 12 ? "PM" : "AM";
        horas.push(`${hora12} ${suf}`);
      }
      return horas;
    },
    calcularBloqueStyle(bloque) {
      const inicio = new Date(bloque.start || bloque.startIso || bloque.fechaKey);
      const fin = new Date(bloque.end || bloque.endIso || bloque.start);
      if (Number.isNaN(inicio.getTime()) || Number.isNaN(fin.getTime())) {
        return { left: "0%", width: "0%" };
      }
      const baseMin = this.timelineConfig.inicio * 60;
      const totalMin = (this.timelineConfig.fin - this.timelineConfig.inicio) * 60;
      const inicioMin = inicio.getHours() * 60 + inicio.getMinutes();
      const finMin = fin.getHours() * 60 + fin.getMinutes();
      const offset = Math.max(inicioMin - baseMin, 0);
      const dur = Math.max(finMin - inicioMin, 15);
      return {
        left: `${(offset / totalMin) * 100}%`,
        width: `${(Math.min(dur, totalMin) / totalMin) * 100}%`,
      };
    },
    calcularBloqueLabel(bloque) {
      return `${this.formatearHoraBloque(bloque.start)} – ${this.formatearHoraBloque(bloque.end)}`;
    },
    nombreEnfermero(id) {
      const enf = this.enfermeros.find((e) => e.id === id);
      return enf?.nombreCompleto || "Sin asignar";
    },
    etiquetaOrigen(origen) {
      if (origen === "excepcion") return "Excepción";
      if (origen === "manual") return "Manual";
      return "Plantilla";
    },
    async pausarFecha(fecha, uid = this.selectedEnfermeroId) {
      if (!uid) {
        this.mostrarEstado("Selecciona un esteticista para pausar el día.", "error");
        return;
      }
      await this.bloquearFechaPara(uid, fecha);
    },
    async habilitarFecha(fecha, uid = this.selectedEnfermeroId) {
      if (!uid) {
        this.mostrarEstado("Selecciona un esteticista para habilitar el día.", "error");
        return;
      }
      await this.habilitarFechaPara(uid, fecha);
    },
    async bloquearFechaPara(uid, fecha) {
      if (!fecha) {
        this.mostrarEstado("Selecciona una fecha válida.", "error");
        return;
      }
      this.bloqueando = true;
      try {
        await this.invocarCallable("bloquearDiaCallable", { uid, fecha });
        this.mostrarEstado("Día bloqueado.", "success");
        await this.fetchDisponibilidad();
      } catch (error) {
        console.error("Error al bloquear día:", error);
        this.mostrarEstado("No se pudo bloquear el día.", "error");
      } finally {
        this.bloqueando = false;
      }
    },
    async habilitarFechaPara(uid, fecha) {
      if (!fecha) {
        this.mostrarEstado("Selecciona una fecha válida.", "error");
        return;
      }
      this.habilitando = true;
      try {
        await this.invocarCallable("habilitarDiaCallable", { uid, fecha });
        this.mostrarEstado("Día habilitado nuevamente.", "success");
        await this.fetchDisponibilidad();
      } catch (error) {
        console.error("Error al habilitar día:", error);
        this.mostrarEstado("No se pudo habilitar el día.", "error");
      } finally {
        this.habilitando = false;
      }
    },
    async cargarResumen() {
      if (!this.selectedEnfermeroId) {
        this.resumenPlantilla = null;
        return;
      }
      this.verificandoResumen = true;
      try {
        const payload = await this.invocarCallable("verificarHorariosCallable", {
          uids: [this.selectedEnfermeroId],
          semanas: 2,
        });
        this.resumenPlantilla = payload?.resultados?.[0] || null;
      } catch (error) {
        console.error("Error al auditar disponibilidad:", error);
        this.resumenPlantilla = null;
      } finally {
        this.verificandoResumen = false;
      }
    },
    async purificarHorarios() {
      const ids = this.mostrarTodos
        ? this.enfermeros.map((e) => e.id)
        : this.selectedEnfermeroId
        ? [this.selectedEnfermeroId]
        : [];
      if (!ids.length) {
        this.mostrarEstado("Selecciona al menos un esteticista.", "error");
        return;
      }
      const confirmar = confirm("Esto eliminará duplicados y regenerará la plantilla seleccionada. ¿Continuar?");
      if (!confirmar) return;
      this.purificando = true;
      try {
        await this.invocarCallable("purificarHorariosCallable", { uids: ids });
        this.mostrarEstado("Horarios purificados correctamente.", "success");
        await this.fetchDisponibilidad();
      } catch (error) {
        console.error("Error al purificar horarios:", error);
        this.mostrarEstado("No se pudo purificar. Intenta nuevamente.", "error");
      } finally {
        this.purificando = false;
      }
    },
    formatearResumenFecha(iso) {
      if (!iso) return "N/A";
      const fecha = new Date(iso);
      if (Number.isNaN(fecha.getTime())) return "N/A";
      return fecha.toLocaleString("es-PR", {
        weekday: "short",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit"
      });
    }
  },
  async mounted() {
    await this.cargarEnfermeros();
  }
};
</script>

<style scoped>
.timeline-row {
  position: relative;
  min-height: 42px;
  background-image: linear-gradient(to right, rgba(226, 232, 240, 0.6) 1px, transparent 1px);
  background-size: calc(100% / 14) 100%;
}
.timeline-block {
  position: absolute;
  top: 6px;
  bottom: 6px;
  background-color: #10b981;
  color: white;
  font-size: 0.75rem;
  border-radius: 8px;
  padding: 0.15rem 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 2.5rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.timeline-block span {
  pointer-events: none;
}
</style>
    async bloquearDia() {
      if (!this.selectedEnfermeroId || !this.diaParaBloquear) {
        this.mostrarEstado("Selecciona esteticista y fecha a bloquear.", "error");
        return;
      }
      await this.bloquearFechaPara(this.selectedEnfermeroId, this.diaParaBloquear);
      this.diaParaBloquear = "";
    },
    async habilitarDia() {
      if (!this.selectedEnfermeroId || !this.diaParaHabilitar) {
        this.mostrarEstado("Selecciona esteticista y fecha a habilitar.", "error");
        return;
      }
      await this.habilitarFechaPara(this.selectedEnfermeroId, this.diaParaHabilitar);
      this.diaParaHabilitar = "";
    },
